import { useEffect, useState } from "react";
import {
  CargoData,
  CargoFormData,
} from "../../ducks/app/cargo-dialog/types";
import { useAppDispatch, useSelector } from "../../redux-store";
import {
  selectFormDataForWhenOpeningModal,
  selectInitialFormDataFromCache,
  selectIsCargoDeleted,
  selectIsSaving,
  selectResetFormData,
} from "../../ducks/app/cargo-dialog/selectors";
import { deleteCargo, loadCargo } from "../../ducks/app/cargo-dialog";
import * as cargoDialogActions from "../../ducks/app/cargo-dialog";
import CreateEditShipmentDialog from "./CreateEditShipmentDialog";
import { useStableNavigate } from "../StableNavigateContext";
import { CargoFormDataBase } from "../../ducks/app/cargo-and-template-shared";
import { loadProducts } from "../../ducks/data/products";
import { CargoModalEntityType } from "./CreateEditShipmentDialog/CargoModalEntityType";

type EditShipmentProps = {
  onClose: (props: { formData?: CargoFormData; cargoId?: string }) => void;
  cargoId?: string;
  routeId?: string;
};

const AddEditCargoModalWrapper = (props: EditShipmentProps) => {
  const { cargoId, routeId } = props;
  const [cargoData, setCargoData] = useState<CargoData | null>(null);
  const resetData = useSelector(selectResetFormData);
  const isSaving = useSelector(selectIsSaving);
  const deleted = useSelector((state) =>
    cargoId ? selectIsCargoDeleted(cargoId)(state) : false
  );
  const dispatch = useAppDispatch();
  const formStartingData = useSelector(selectFormDataForWhenOpeningModal);
  const initialDataFromCache = useSelector(selectInitialFormDataFromCache);
  const navigate = useStableNavigate();

  const onClose = ({
    formData,
    cargoId,
  }: {
    formData?: CargoFormDataBase | CargoFormData;
    cargoId?: string;
  }) => {
    setCargoData(null);
    props.onClose({ formData, cargoId });
  };

  useEffect(() => {
    if (cargoId) {
      // The hash can move to another cargo (browser Back, opening another
      // cargo) before this load returns; its result must not fill the dialog
      // for the new one (DORA-390).
      let stale = false;
      dispatch(loadCargo(cargoId)).then((result) => {
        if (stale) return;
        if (loadCargo.fulfilled.match(result) && result.payload === null) {
          // The cargo has been deleted: nothing to edit, close the dialog.
          navigate(
            { hash: "", search: window.location.search },
            { replace: true }
          );
          return;
        }
        if (result.payload) {
          const data = (result.payload as any).data as CargoData; // TODO: can't infer correct type
          setCargoData(data);
        }
      });
      return () => {
        stale = true;
      };
    }
  }, [cargoId, dispatch, navigate]);

  // Until the new cargo has loaded, cargoData still holds the previous one;
  // never hand it to the dialog under a different cargoId (or to a new cargo).
  const currentCargoData =
    cargoId && cargoData?.id === cargoId ? cargoData : null;

  const handleSubmit = async (
    formData: CargoData & {
      keepAlive?: boolean;
      submitType: "CREATE" | "UPDATE" | "DUPLICATE" | "CREATE_AND_MAKE_COPY";
      deleteStopsWithEvents?: boolean;
      cargoIdToCopyInvoiceLinesFrom: string | null;
      cargoIdToCopyFilesFrom: string | null;
      resetInvoiceLineQuantities?: boolean;
      dropDeletedExtraCo2Types?: boolean;
    }
  ) => {
    const res = await dispatch(
      cargoDialogActions.submit({
        ...formData,
        routeId,
        submitType: formData.submitType,
      })
    ).unwrap();
    // A refused save (e.g. lines whose CO2 type was deleted) keeps the
    // dialog open for the user to answer.
    if (!res.keepAlive && !res.error) {
      setCargoData(null);
      navigate({ hash: "", search: window.location.search });
    }

    return res;
  };

  const handleDelete = async () => {
    if (!cargoId) {
      console.error("No cargo template id to delete");
      return;
    }
    try {
      await dispatch(deleteCargo(cargoId)).unwrap();
    } catch {
      // deleteCargo has already reported the failure and shown the error
      // toast; keep the dialog open so the user sees the cargo wasn't deleted.
      return;
    }
    navigate({ hash: "", search: window.location.search }, { replace: true });
  };

  const [productsLoaded, setProductsLoaded] = useState(false);
  useEffect(() => {
    if (productsLoaded) {
      return;
    }
    dispatch(loadProducts())
      .unwrap()
      .then(() => setProductsLoaded(true))
      // loadProducts has already reported the failure and shown the toast;
      // without this the rejection escapes as an unhandled one (DISPATCHER-4).
      .catch(() => {});
  }, [dispatch, productsLoaded]);

  return productsLoaded &&
    (currentCargoData || (!cargoId && formStartingData)) ? (
    <CreateEditShipmentDialog
      // The form is initialised once from initialData at mount: a different
      // cargo needs a fresh dialog, not the old form with a new id.
      key={cargoId ?? "new"}
      resetData={resetData!}
      initialData={currentCargoData || formStartingData!}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      onClose={onClose}
      routeId={routeId}
      isSaving={isSaving}
      createNew={!cargoId}
      entityType={CargoModalEntityType.CARGO}
      initialDataFromCache={!cargoId && initialDataFromCache}
      deleted={deleted}
    />
  ) : null;
};

export default AddEditCargoModalWrapper;
